import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import * as Sentry from '@sentry/browser'

const STORAGE_KEY = 'native.fcm_token'
const REGISTRATION_TIMEOUT_MS = 20000

let currentToken = null
let listenersRegistered = false

// Resolver of the currently awaited registration attempt (see registerNativePush).
// The Capacitor bridge reports the FCM token asynchronously via the 'registration'
// listener, so we bridge listener + POST result back into an awaitable promise.
let pendingRegistration = null

// Load any previously saved token so we can properly delete it on unregister after app restarts
try {
  const stored = typeof window !== 'undefined' ? window.localStorage?.getItem(STORAGE_KEY) : null
  if (stored) {
    currentToken = stored
  }
} catch (_) {
  // ignore storage errors
}

export function getCachedNativeToken() {
  return currentToken
}

function readCsrfToken() {
  try {
    const meta = document.head.querySelector('meta[name="csrf-token"]')
    if (meta && meta.content) {
      return meta.content
    }
  } catch (_) {}
  // Fallback for older code that set window.Laravel.csrfToken
  try {
    // eslint-disable-next-line no-undef
    if (typeof window !== 'undefined' && window.Laravel?.csrfToken) {
      // eslint-disable-next-line no-undef
      return window.Laravel.csrfToken
    }
  } catch (_) {}
  return undefined
}

export function buildDefaultHeaders() {
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
  const csrf = readCsrfToken()
  if (csrf) {
    headers['X-CSRF-TOKEN'] = csrf
  }
  return headers
}

function storeToken(token) {
  currentToken = token
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      if (token) {
        window.localStorage.setItem(STORAGE_KEY, token)
      } else {
        window.localStorage.removeItem(STORAGE_KEY)
      }
    }
  } catch (_) {
    // ignore storage errors
  }
}

async function deleteTokenFromApi(token) {
  const res = await fetch('/user/fcm-token', {
    method: 'DELETE',
    headers: buildDefaultHeaders(),
    body: JSON.stringify({ token }),
    credentials: 'include'
  })
  if (!res.ok) {
    throw new Error(`Delete FCM token failed with status ${res.status} (${res.url})`)
  }
}

async function saveTokenToApi(newToken) {
  // Intentionally no "token unchanged" shortcut: the server row may have been
  // removed (auto-disable, cleanup) while the device token stayed stable —
  // re-posting is idempotent and heals exactly that state.
  if (currentToken != null && currentToken !== newToken) {
    try {
      await deleteTokenFromApi(currentToken)
    } catch (error) {
      // Old token cleanup is best-effort; the fresh registration matters.
      Sentry.captureException(error)
    }
  }

  const res = await fetch('/user/fcm-token', {
    method: 'POST',
    headers: buildDefaultHeaders(),
    body: JSON.stringify({ token: newToken }),
    credentials: 'include'
  })

  if (!res.ok) {
    throw new Error(`Save FCM token failed with status ${res.status} (${res.url})`)
  }

  storeToken(newToken)
}

function settlePendingRegistration(error = null) {
  if (!pendingRegistration) {
    return false
  }

  const pending = pendingRegistration
  pendingRegistration = null
  clearTimeout(pending.timer)

  if (error) {
    pending.reject(error instanceof Error ? error : new Error(JSON.stringify(error)))
  } else {
    pending.resolve('registered')
  }

  return true
}

/**
 * Decide where a tapped push notification should navigate.
 *
 * A server-provided absolute deep link (`data.url`) always wins — series
 * invitations point at the events overview, not a series master's (non-existent)
 * member detail page. Otherwise fall back to the legacy eventId/rosterId routes.
 *
 * @param {{ url?: string, eventId?: string|number, rosterId?: string|number }} [data]
 * @returns {string|null}
 */
export function resolvePushTargetUrl(data) {
  if (!data) {
    return null
  }

  if (data.url) {
    return data.url
  }

  const routes = (typeof window !== 'undefined' && window.MembergyRoutes) || {}

  if (data.eventId && routes.eventShow) {
    return routes.eventShow.replace('__EVENT_ID__', data.eventId)
  }

  if (data.rosterId && routes.rosterShow) {
    return routes.rosterShow.replace('__ROSTER_ID__', data.rosterId)
  }

  return null
}

export function initNativePush() {
  if (!Capacitor.isNativePlatform()) {
    return
  }

  if (listenersRegistered) {
    return
  }

  try {
    PushNotifications.addListener('registration', async(token) => {
      try {
        if (!token?.value) {
          throw new Error('Native push registration returned an empty token')
        }
        await saveTokenToApi(token.value)
        settlePendingRegistration()
      } catch (error) {
        if (!settlePendingRegistration(error)) {
          // Nobody is awaiting (unsolicited registration event) – still report.
          Sentry.captureException(error)
        }
      }
    })

    PushNotifications.addListener('registrationError', (error) => {
      if (!settlePendingRegistration(error)) {
        Sentry.captureException(new Error(`Native push registration error: ${JSON.stringify(error)}`))
      }
    })

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      // For now, just log the notification
      // You can extend this to update UI/badges etc.
      console.log('Push received', notification)
    })

    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      // Mirror legacy behavior: if an eventId is provided, let the app decide how to navigate.
      // We dispatch a custom DOM event so app code can hook into it and navigate as needed.
      try {
        const url = resolvePushTargetUrl(action?.notification?.data)
        if (url && typeof window !== 'undefined') {
          window.Livewire.navigate(url)
        }
      } catch (err) {
        // Fallback to logging only
        console.log('Push action performed', action)
      }
    })

    listenersRegistered = true
  } catch (error) {
    Sentry.captureException(error)
  }
}

export async function checkNativePushPermission() {
  if (!Capacitor.isNativePlatform()) {
    return null
  }

  const status = await PushNotifications.checkPermissions()

  return status.receive
}

/**
 * Registers this device for native push.
 *
 * @returns {Promise<'registered'|'denied'|'unsupported'>} Resolves once the FCM
 *   token has been persisted server-side. 'denied' means the user must allow
 *   notifications in the system settings. Systemic failures (registration error,
 *   token save failure, timeout) reject with an Error.
 */
export async function registerNativePush() {
  if (!Capacitor.isNativePlatform()) {
    return 'unsupported'
  }

  let permStatus = await PushNotifications.checkPermissions()

  if (permStatus.receive !== 'granted') {
    permStatus = await PushNotifications.requestPermissions()
  }

  if (permStatus.receive !== 'granted') {
    return 'denied'
  }

  const result = new Promise((resolve, reject) => {
    pendingRegistration = {
      resolve,
      reject,
      timer: setTimeout(
        () => settlePendingRegistration(new Error('Native push registration timed out')),
        REGISTRATION_TIMEOUT_MS
      )
    }
  })

  try {
    await PushNotifications.register()
  } catch (error) {
    settlePendingRegistration(error)
  }

  return result
}

export async function unregisterNativePush() {
  if (!Capacitor.isNativePlatform()) {
    return 'unsupported'
  }

  await PushNotifications.unregister()

  if (currentToken != null) {
    await deleteTokenFromApi(currentToken)
    storeToken(null)
  }

  return 'unregistered'
}
